import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/utils/supabase/client';
import { format, subMonths } from 'date-fns';

const DashboardContext = createContext<any>(null);

export const useDashboard = () => useContext(DashboardContext);

const getOrganizationId = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return null;
  }

  const { data, error } = await supabase
    .from('organizations')
    .select('id')
    .eq('owner_id', user.id)

  if (error) {
    console.error('Error fetching organization:', error);
    return null;
  }
  
  return data?.[0]?.id;
};

export function DashboardClientWrapper({ children }: { children: React.ReactNode }) {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const organizationId = await getOrganizationId();

      if (!organizationId) {
        setLoading(false);
        return;
      }
      
      const queries = [
        supabase.from('appointments').select('*, doctors(*), patients(*)').eq('organization_id', organizationId),
        supabase.from('patients').select('*').eq('organization_id', organizationId),
        supabase.from('payments').select('*').eq('organization_id', organizationId),
        supabase.from('expenses').select('*').eq('organization_id', organizationId),
        supabase.from('admissions').select('*, patients(*), doctors(*)').eq('organization_id', organizationId),
        supabase.from('prescriptions').select('*').eq('organization_id', organizationId),
        supabase.from('patient_vitals').select('*').eq('organization_id', organizationId),
        supabase.from('doctors').select('*').eq('organization_id', organizationId),
        supabase.from('medical_records').select('*, patients(*), doctors(*)').eq('organization_id', organizationId),
      ];

      const results = await Promise.all(queries);

      const [appointmentsRes, patientsRes, paymentsRes, expensesRes, admissionsRes, prescriptionsRes, vitalsRes, doctorsRes, medicalRecordsRes] = results;

      // It's better to handle errors properly, but for now, let's just log them
      if (appointmentsRes.error) console.error('Appointments fetch error:', appointmentsRes.error);
      if (patientsRes.error) console.error('Patients fetch error:', patientsRes.error);
      if (paymentsRes.error) console.error('Payments fetch error:', paymentsRes.error);
      if (expensesRes.error) console.error('Expenses fetch error:', expensesRes.error);
      if (admissionsRes.error) console.error('Admissions fetch error:', admissionsRes.error);
      if (prescriptionsRes.error) console.error('Prescriptions fetch error:', prescriptionsRes.error);
      if (vitalsRes.error) console.error('Vitals fetch error:', vitalsRes.error);
      if (doctorsRes.error) console.error('Doctors fetch error:', doctorsRes.error);
      if (medicalRecordsRes.error) console.error('Medical records fetch error:', medicalRecordsRes.error);

      const appointments = appointmentsRes.data || [];
      const patients = patientsRes.data || [];
      const payments = paymentsRes.data || [];
      const expenses = expensesRes.data || [];
      const admissions = admissionsRes.data || [];
      const prescriptions = prescriptionsRes.data || [];
      const vitals = vitalsRes.data || [];
      const doctors = doctorsRes.data || [];
      const medicalRecords = medicalRecordsRes.data || [];

      const todayStr = format(new Date(), 'yyyy-MM-dd');
      const todayAppointments = appointments.filter(a => a.appointment_date === todayStr);
      const todayRevenue = payments.filter(p => p.created_at?.slice(0, 10) === todayStr).reduce((sum, p) => sum + (p.amount_paid || 0), 0);
      const todayPatients = patients.filter(p => p.created_at?.slice(0, 10) === todayStr).length;
      const todayAdmissions = admissions.filter(a => a.admission_date?.slice(0, 10) === todayStr).length;
      const todayPrescriptions = prescriptions.filter(p => p.created_at?.slice(0, 10) === todayStr).length;
      const todayVitals = vitals.filter(v => v.created_at?.slice(0, 10) === todayStr).length;

      const months = Array.from({ length: 6 }, (_, i) => format(subMonths(new Date(), 5 - i), 'yyyy-MM'));
      const revenueVsExpenses = months.map(month => {
        const monthPayments = payments.filter(p => p.created_at?.startsWith(month));
        const monthExpenses = expenses.filter(e => e.expense_date?.startsWith(month));
        return {
          month: format(new Date(month + '-01'), 'MMM yyyy'),
          revenue: monthPayments.reduce((sum, p) => sum + (p.amount_paid || 0), 0),
          expenses: monthExpenses.reduce((sum, e) => sum + (e.amount || 0), 0),
        };
      });

      const currentMonth = format(new Date(), 'yyyy-MM');
      const specialtyMap: Record<string, number> = {};
      appointments.filter(a => a.appointment_date?.startsWith(currentMonth)).forEach(a => {
        const spec = a.doctors?.specialization || 'Other';
        specialtyMap[spec] = (specialtyMap[spec] || 0) + 1;
      });
      const colorPalette = ['#3b82f6', '#10b981', '#f59e42', '#ef4444', '#a78bfa', '#f472b6', '#14b8a6'];
      const appointmentsBySpecialty = Object.entries(specialtyMap).map(([name, value], i) => ({ name, value, color: colorPalette[i % colorPalette.length] }));

      const methodMap: Record<string, { amount: number, count: number }> = {};
      payments.forEach(p => {
        const method = p.payment_method || 'Other';
        methodMap[method] = methodMap[method] || { amount: 0, count: 0 };
        methodMap[method].amount += p.amount_paid || 0;
        methodMap[method].count += 1;
      });
      const totalPayments = payments.reduce((sum, p) => sum + (p.amount_paid || 0), 0);
      const paymentMethods = Object.entries(methodMap).map(([method, { amount, count }]) => ({
        method,
        amount,
        percentage: totalPayments ? Math.round((amount / totalPayments) * 100) : 0,
        value: count,
        name: method,
      }));

      const weeklyAppointments = Array.from({ length: 7 }, (_, i) => {
        const date = format(new Date(Date.now() - (6 - i) * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
        const dayAppointments = appointments.filter(a => a.appointment_date === date);
        return {
          day: format(new Date(date), 'EEE'),
          scheduled: dayAppointments.length,
          completed: dayAppointments.filter(a => a.status === 'completed').length,
          cancelled: dayAppointments.filter(a => a.status === 'cancelled').length,
        };
      });

      const patientGrowth = months.map(month => {
        const monthPatients = patients.filter(p => p.created_at?.startsWith(month));
        return {
          month: format(new Date(month + '-01'), 'MMM yyyy'),
          total: monthPatients.length,
          new: monthPatients.length,
        };
      });

      const categoryMap: Record<string, { amount: number, budget: number }> = {};
      expenses.filter(e => e.expense_date?.startsWith(currentMonth)).forEach(e => {
        const cat = e.category || 'Other';
        categoryMap[cat] = categoryMap[cat] || { amount: 0, budget: 10000 };
        categoryMap[cat].amount += e.amount || 0;
      });
      const expenseCategories = Object.entries(categoryMap).map(([category, { amount, budget }]) => ({
        category,
        amount,
        percentage: budget ? Math.round((amount / budget) * 100) : 0,
        budget,
      }));

      const doctorPerformance = doctors.map(doc => {
        const docAppointments = appointments.filter(a => a.doctor_id === doc.id);
        const docPatients = new Set(docAppointments.map(a => a.patient_id)).size;
        const docRevenue = payments.filter(p => docAppointments.some(a => a.id === p.appointment_id)).reduce((sum, p) => sum + (p.amount_paid || 0), 0);
        return {
          name: doc.name,
          appointments: docAppointments.length,
          patients: docPatients,
          revenue: docRevenue,
          rating: 4.5, // This is hardcoded, should be calculated
          specialty: doc.specialization,
        };
      });

      const vitalsData = [
        { parameter: 'BP', average: 120, normal: 80, abnormal: 10, critical: 2 },
        { parameter: 'Pulse', average: 75, normal: 85, abnormal: 8, critical: 1 },
        { parameter: 'Temp', average: 98.6, normal: 90, abnormal: 7, critical: 0 },
      ];

      const deptMap: Record<string, { admissions: number, discharges: number, occupancy: number, occupied: number, total: number, waiting: number }> = {};
      admissions.forEach(a => {
        const dept = a.department || 'General';
        deptMap[dept] = deptMap[dept] || { admissions: 0, discharges: 0, occupancy: 0, occupied: 0, total: 40, waiting: 0 };
        deptMap[dept].admissions += 1;
      });
      const admissionStats = Object.entries(deptMap).map(([department, stats]) => ({
        department,
        ...stats,
      }));

      const recentMedicalRecords = medicalRecords.slice(-10).map(r => ({
        id: r.id,
        patient: r.patients?.name || 'Unknown',
        doctor: r.doctors?.name || 'Unknown',
        date: r.created_at ? format(new Date(r.created_at), 'yyyy-MM-dd') : '',
        diagnosis: r.diagnosis || '',
        time: r.created_at ? format(new Date(r.created_at), 'HH:mm') : '',
      }));

      setData({
        todayStats: {
          appointments: todayAppointments.length,
          revenue: todayRevenue,
          patients: todayPatients,
          admissions: todayAdmissions,
          prescriptions: todayPrescriptions,
          vitalChecks: todayVitals,
        },
        revenueVsExpenses,
        appointmentsBySpecialty,
        paymentMethods,
        weeklyAppointments,
        patientGrowth,
        expenseCategories,
        doctorPerformance,
        vitalsData,
        admissionStats,
        recentMedicalRecords,
      });
      setLoading(false);
    } catch (error) {
      console.error('An unexpected error occurred in fetchDashboardData:', error);
      setLoading(false);
    }
  };

  return (
    <DashboardContext.Provider value={{ data, loading, fetchDashboardData }}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardClientWrapper; 